import { cn } from "@repo/shared/utils";
import type { AgendaItemUserVote, AgendaItemVotes, User } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";

// const reasonsForAbstenance: string[] = [
//   "Conflict of interest",
//   "Lack of information",
//   "Legal or ethical concern",
// ];

export type VoteChangeSource =
  | "type"
  | "anonymousVote"
  | "vote"
  | "unanimous"
  | "clearAll";

export interface AgendaItemVotesFormProps {
  disabled?: boolean;
  votes: Partial<AgendaItemVotes> | undefined;
  votingUsers?: User[];
  absentUsers?: User[];
  onVotesChange?: (item: AgendaItemVotes, source: VoteChangeSource) => void;
}

export const AgendaItemVotesForm = ({
  disabled = false,
  votes,
  votingUsers = [],
  absentUsers = [],
  onVotesChange,
}: AgendaItemVotesFormProps) => {
  return (
    <>
      <SelectInput
        disabled={disabled}
        label="Type of Voting"
        placeholder="Select a type"
        value={votes?.type ?? ""}
        onValueChange={(value) => {
          onVotesChange?.(
            {
              yes: 0,
              no: 0,
              abstain: 0,
              ...votes,
              type: value as AgendaItemVotes["type"],
            },
            "type"
          );
        }}
        options={[
          <SelectItem key="voice" value="voice">
            By Voice
          </SelectItem>,
          <SelectItem key="hand" value="hand">
            By Hand
          </SelectItem>,
          <SelectItem key="rollcall" value="rollcall">
            By Roll Call
          </SelectItem>,
          <SelectItem key="ballot" value="ballot">
            By Ballot
          </SelectItem>,
        ]}
      />
      {votes?.type === "ballot" && (
        <div key="private" className="flex flex-row gap-x-4">
          <Input
            id="reviewVotesYes"
            disabled={disabled}
            label="Yes"
            value={votes.yes ?? "0"}
            onChange={(evt) => {
              onVotesChange?.(
                {
                  no: 0,
                  abstain: 0,
                  ...votes,
                  type: "ballot",
                  users: undefined,
                  yes: Number(evt.target.value) || 0,
                },
                "anonymousVote"
              );
            }}
          />
          <Input
            id="reviewVotesNo"
            disabled={disabled}
            label="No"
            value={votes?.no ?? "0"}
            onChange={(value) => {
              onVotesChange?.(
                {
                  yes: 0,
                  abstain: 0,
                  ...votes,
                  type: "ballot",
                  users: undefined,
                  no: Number(value.target.value) || 0,
                },
                "anonymousVote"
              );
            }}
          />
          <Input
            id="reviewVotesAbstain"
            disabled={disabled}
            label="Abstain"
            value={votes?.abstain ?? "0"}
            onChange={(value) => {
              onVotesChange?.(
                {
                  yes: 0,
                  no: 0,
                  ...votes,
                  type: "ballot",
                  users: undefined,
                  abstain: Number(value.target.value) || 0,
                },
                "anonymousVote"
              );
            }}
          />
        </div>
      )}
      {votes?.type !== "ballot" && (
        <div key="public" className="flex flex-col gap-y-4">
          <div className="flex flex-row items-center gap-x-4">
            <div className="flex-shrink-0 w-[200px]" />
            <Button
              disabled={disabled}
              type="button"
              variant="tertiary"
              className="h-8"
              onClick={() => {
                onVotesChange?.(
                  {
                    type: votes?.type ?? "voice",
                    yes: votingUsers.length,
                    no: 0,
                    abstain: 0,
                    users: votingUsers
                      .filter((user) => !absentUsers?.includes(user))
                      .map((user) => ({
                        userId: user.id,
                        vote: "yes",
                      })),
                  },
                  "unanimous"
                );
              }}
            >
              Unanimous
            </Button>
            <Button
              disabled={disabled}
              type="button"
              variant="destructive"
              className="h-8"
              onClick={() => {
                onVotesChange?.(
                  {
                    type: votes?.type ?? "voice",
                    yes: 0,
                    no: 0,
                    abstain: 0,
                    users: [],
                  },
                  "clearAll"
                );
              }}
            >
              Clear All
            </Button>
          </div>
          {votingUsers.map((user) => {
            if (votes?.type === "ballot") return; // typescript's not that smart

            const userVote = votes?.users?.find(
              (review) => review.userId === user.id
            );

            const update = ({
              vote,
              reason,
            }: {
              vote?: "yes" | "no" | "abstain";
              reason?: string;
            }) => {
              const users =
                votes?.users?.filter((review) => review.userId !== user.id) ??
                [];

              // if equal, unselect option
              if (!vote || userVote?.vote !== vote) {
                const newVote: AgendaItemUserVote = {
                  vote: "abstain",
                  reason: undefined,
                  ...userVote,
                  userId: user.id,
                };
                if (reason != null) newVote.reason = reason;
                if (vote) newVote.vote = vote;

                users.push(newVote);
              }

              // recompute total
              let yes = 0;
              let no = 0;
              let abstain = 0;
              for (const review of users) {
                if (review.vote === "yes") yes++;
                else if (review.vote === "no") no++;
                else if (review.vote === "abstain") abstain++;
              }

              // update item
              onVotesChange?.(
                {
                  type: votes?.type ?? "voice",
                  yes,
                  no,
                  abstain,
                  users,
                },
                "vote"
              );
            };

            return (
              <div key={user.id} className="flex flex-row items-center gap-x-4">
                <div className="flex-shrink-0 w-[200px] font-bold text-right">
                  {user.firstName} {user.lastName}
                </div>
                <Button
                  type="button"
                  tooltip={
                    absentUsers.includes(user) ? "User is absent" : undefined
                  }
                  disabled={disabled || absentUsers.includes(user)}
                  className={cn(
                    "border-[2px] border-tertiary",
                    userVote?.vote === "yes"
                      ? "bg-tertiary hover:bg-tertiary/90"
                      : "text-tertiary bg-transparent hover:bg-transparent"
                  )}
                  onClick={() => update({ vote: "yes" })}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  tooltip={
                    absentUsers.includes(user) ? "User is absent" : undefined
                  }
                  disabled={disabled || absentUsers.includes(user)}
                  className={cn(
                    "border-[2px] border-destructive",
                    userVote?.vote === "no"
                      ? "bg-destructive hover:bg-destructive/90"
                      : "text-destructive bg-transparent hover:bg-transparent"
                  )}
                  onClick={() => update({ vote: "no" })}
                >
                  No
                </Button>
                <Button
                  type="button"
                  tooltip={
                    absentUsers.includes(user) ? "User is absent" : undefined
                  }
                  disabled={disabled || absentUsers.includes(user)}
                  className={cn(
                    "border-[2px] border-secondary",
                    userVote?.vote === "abstain"
                      ? "bg-secondary hover:bg-secondary/90"
                      : "text-secondary bg-transparent hover:bg-transparent"
                  )}
                  onClick={() => update({ vote: "abstain" })}
                >
                  Abstain
                </Button>
                <Input
                  id={user.id + "-voteReason"}
                  containerStyle="mt-0"
                  disabled={disabled || absentUsers.includes(user)}
                  className={cn(
                    "w-[250px]",
                    userVote?.vote === "abstain"
                      ? "outline outline-destructive outline-2"
                      : ""
                  )}
                  placeholder={
                    userVote?.vote === "abstain"
                      ? "Required if for conflict of interest"
                      : "Reasoning / Conflict of interest"
                  }
                  value={userVote?.reason ?? ""}
                  onChange={(evt) => update({ reason: evt.target.value })}
                />
              </div>
            );
          })}
          <div className="flex flex-row items-center gap-x-4">
            <div className="flex-shrink-0 w-[200px] text-right font-bold">
              Total:
            </div>
            <div className="text-tertiary font-bold">
              Yes: {votes?.yes ?? 0}
            </div>
            <div className="text-destructive font-bold">
              No: {votes?.no ?? 0}
            </div>
            <div className="text-secondary font-bold">
              Abstain: {votes?.abstain ?? 0}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
